import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, foldToOther, frameNote, isEmpty } from '../frame';
import {
  animation,
  AREA_OPACITY,
  axisTooltip,
  baseGrid,
  categoryAxisX,
  emphasisFocus,
  lineEndLabel,
  legendFor,
  lineStyle,
  markerStyle,
  MARKER_SIZE,
  valueAxisY,
} from '../options';

/**
 * A measure over time.
 *
 * One series gets a 10% wash under the line — enough to give the trend weight
 * without turning it into a block. Several series stay as bare lines: stacked
 * washes muddy each other and overlapping ones lie about the values underneath.
 *
 * There is deliberately no second y-axis here, and there never should be. Two
 * measures on different scales aligned on one plot invent a correlation that
 * isn't in the data. Two measures means two charts.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const MAX_SERIES = 8;
/** Past four converging lines, end labels detach from their lines and read as
 *  noise — the legend and the crosshair carry identity from there. */
const DIRECT_LABEL_LIMIT = 4;

/**
 * How the area under the lines is filled:
 *   'trend' — the existing default: a wash under a lone series, bare lines when
 *             there are several (stacked washes muddy each other).
 *   'none'  — a pure line chart, no fill even for one series.
 *   'all'   — an area chart: every series gets the wash, at a lower opacity so
 *             overlaps stay readable.
 */
type Fill = 'trend' | 'none' | 'all';

interface LineOpts {
  fill: Fill;
  /** Smooth splines vs straight segments vs a right-angled step. */
  curve?: 'linear' | 'smooth' | 'step';
  /** Stack the series to a running total (stacked area). */
  stack?: boolean;
}

const lineView = (
  data: ChartData,
  fields: ChartFields | undefined,
  theme: ReturnType<typeof useTheme>,
  opts: LineOpts,
): { option: EChartsOption; note?: string } | null => {
  const { fill, curve = 'linear', stack = false } = opts;
  const frame = foldToOther(buildFrame(data, fields), MAX_SERIES);
  if (isEmpty(frame)) return null;
  if (frame.categories.length < 2) return null;

  const single = frame.series.length === 1;
  const directLabels = frame.series.length <= DIRECT_LABEL_LIMIT && !stack;
  const crowded = frame.categories.length > 12;

  const option: EChartsOption = {
    ...animation,
    grid: {
      ...baseGrid({ legend: !single, rotatedLabels: crowded && !frame.temporal }),
      right: directLabels ? 48 : 12,
    },
    xAxis: {
      ...categoryAxisX(theme, frame, { rotate: crowded && !frame.temporal }),
      boundaryGap: false,
    },
    yAxis: valueAxisY(theme, { name: frame.measureName }),
    legend: legendFor(theme, frame.series.length, 'line'),
    tooltip: axisTooltip(theme, frame),
    series: frame.series.map((s, i) => {
      const color = seriesColor(theme, i);
      // Stacked areas need an opaque-ish fill or the layers vanish; overlapping
      // washes stay light. A lone 'trend' series keeps its 10% wash.
      const areaOpacity = stack
        ? 0.85
        : fill === 'all'
          ? single
            ? AREA_OPACITY
            : AREA_OPACITY * 0.7
          : fill === 'trend' && single
            ? AREA_OPACITY
            : 0;
      return {
        type: 'line' as const,
        name: s.name,
        data: s.values,
        smooth: curve === 'smooth',
        step: curve === 'step' ? ('middle' as const) : false,
        stack: stack ? 'total' : undefined,
        connectNulls: stack,
        showSymbol: false,
        symbol: 'circle' as const,
        symbolSize: MARKER_SIZE,
        lineStyle: lineStyle(color),
        itemStyle: markerStyle(theme, color),
        areaStyle: areaOpacity > 0 ? { color, opacity: areaOpacity } : undefined,
        endLabel: directLabels ? lineEndLabel(theme) : { show: false },
        labelLayout: { hideOverlap: true },
        emphasis: emphasisFocus,
      };
    }),
  };

  return { option, note: frameNote(frame) };
};

/** A measure over time — a lone series gets a wash, several stay bare lines. */
export const TrendChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => lineView(data, fields, theme, { fill: 'trend' }), [data, theme, fields]);
  if (!view) return <Empty message="Not enough points to draw a trend" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Trend chart" />
    </Plot>
  );
};

/** A pure line chart — no area fill, several series compared as bare lines. */
export const LineChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => lineView(data, fields, theme, { fill: 'none' }), [data, theme, fields]);
  if (!view) return <Empty message="Not enough points to draw a line" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Line chart" />
    </Plot>
  );
};

/** A smoothed (spline) line chart — rounded curves through the points. */
export const SplineChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => lineView(data, fields, theme, { fill: 'none', curve: 'smooth' }), [data, theme, fields]);
  if (!view) return <Empty message="Not enough points to draw a line" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Spline chart" />
    </Plot>
  );
};

/** A step line — the value holds flat, then jumps: for counts that change in steps. */
export const StepLineChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => lineView(data, fields, theme, { fill: 'none', curve: 'step' }), [data, theme, fields]);
  if (!view) return <Empty message="Not enough points to draw a line" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Step line chart" />
    </Plot>
  );
};

/** An area chart — every series filled with a wash. */
export const AreaChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => lineView(data, fields, theme, { fill: 'all' }), [data, theme, fields]);
  if (!view) return <Empty message="Not enough points to draw an area" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Area chart" />
    </Plot>
  );
};

/** A stacked area — series stacked to a running total, each layer filled. */
export const StackedAreaChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => lineView(data, fields, theme, { fill: 'all', stack: true }), [data, theme, fields]);
  if (!view) return <Empty message="Not enough points to draw an area" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Stacked area chart" />
    </Plot>
  );
};
